import type { Envelope, CardUnion, CanonicalError } from "./types";
import { CARD_KINDS } from "@/generated/copilot-contracts";

/** The kinds the parser lets through, from the generated contract rather than
 * from a second hand-written list. The list this replaced held ten values
 * while `contracts/copilot/cards.schema.json` published twelve, so
 * `hitl.request` and `hitl.decision` were rejected here and logged as unknown.
 * Neither has a component registered, so both still render nothing — what
 * changes is where that is decided: the registry, not the parser (SCRUM-305). */
const KNOWN_KINDS: ReadonlySet<string> = new Set(CARD_KINDS);

function isEnvelopeShape(v: unknown): v is {
  llm_summary: string;
  has_card: boolean;
  card_kind: string | null;
  card: unknown;
  has_error: boolean;
  error?: CanonicalError | null;
} {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.llm_summary === "string" &&
    typeof o.has_card === "boolean" &&
    typeof o.has_error === "boolean" &&
    "card" in o &&
    "card_kind" in o
  );
}

/** The envelope a value already parsed carries, or null for another shape. */
function envelopeOf(parsed: unknown): Envelope | null {
  if (!isEnvelopeShape(parsed)) return null;

  // Backend's card payload doesn't include `kind` (it's a Pydantic ClassVar,
  // not an instance field, and is dropped by `model_dump`). The discriminator
  // surfaces one level up as `card_kind`. Inject it into the card object so
  // the front-side discriminated union + CardRegistry lookup work as expected.
  //
  // For unknown kinds we *preserve* the envelope (with `card: null`) instead
  // of returning null — soft-error envelopes and chat_response (text-only,
  // `has_card: false`) need access to `error` / `llm_summary` to render
  // gracefully. Returning null here would force the consumer into the legacy
  // switch's generic "Resultado: {toolName}" JSON dump fallback.
  let card: CardUnion | null = null;
  if (parsed.has_card && parsed.card && typeof parsed.card === "object") {
    if (!parsed.card_kind || !KNOWN_KINDS.has(parsed.card_kind)) {
      console.warn(
        "envelope: unknown card_kind, falling back to legacy renderer",
        parsed.card_kind,
      );
      // Leave card as null so the renderer sees has_card === effectively false.
    } else {
      card = {
        ...(parsed.card as Record<string, unknown>),
        kind: parsed.card_kind,
      } as CardUnion;
    }
  }

  return {
    llm_summary: parsed.llm_summary,
    card,
    error: parsed.has_error ? (parsed.error ?? null) : null,
  };
}

export function parseEnvelope(raw: unknown): Envelope | null {
  if (typeof raw !== "string" || raw.length === 0) return null;

  try {
    return envelopeOf(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** The card an envelope-emitting tool produced, whichever way the transport
 * delivered the envelope: as a JSON string, or already parsed as an object —
 * the copilot SSE parses `tool-output-available` outputs before it yields
 * them (SCRUM-285). Null for a legacy result that is not an envelope. */
export function envelopeCardOf(output: unknown): CardUnion | null {
  if (typeof output === "string") return parseEnvelope(output)?.card ?? null;
  return envelopeOf(output)?.card ?? null;
}
